import { pipe, startsWith, endsWith, equals, pathOr } from 'ramda';
import { HTML_TAG } from '@fmpedia/enums';

function isString(val) {
  return typeof val === 'string' || val instanceof String
}

function isObject(val) {
  return typeof val === 'object' && !isArray(val) && val !== null
}

function isArray(val) {
  if (!val) return false

  return Array.isArray(val)
}

function hasParentWith(el, { className, attribute, id, refEl }) {
  return !!getParentWith(el, { className, attribute, id, refEl })
}

function getParentWith(el, { className, attribute, id, refEl }) {
  function getParent(el) {
    const targetEl = el.parentElement;

    if (!targetEl) {
      return false
    }

    const isFound =
      Array.from(targetEl.classList).includes(className) ||
      (targetEl.hasAttribute && targetEl.hasAttribute(attribute)) ||
      targetEl.id === id ||
      targetEl === refEl;

    return isFound ? targetEl : getParent(targetEl)
  }
  return getParent(el)
}

function focusOnElementByDataRef(dataRef) {
  const el = getElByDataRef(dataRef);
  if (!el || !el.focus || document.activeElement === el) return

  el.focus();
}

function getElByDataRef(ref, elementToSearchIn = null) {
  return (elementToSearchIn || document).querySelector(`[data-ref="${ref}"]`)
}

async function waitForElAppearsInDom({
  dataRef,
  selector,
  timeout = 5000,
  elementToSearchIn = null // not checked but may be useful
} = {}) {
  if (!dataRef && !selector) return null

  const timeoutPromise = new Promise((resolve) =>
    setTimeout(() => resolve(false), timeout)
  );
  const checkDomUpdatesPromise = new Promise((resolve) => {
    let isElementFound = false;

    const isElExistsInDomInitially = dataRef
      ? !!getElByDataRef(dataRef, elementToSearchIn)
      : (elementToSearchIn || document).querySelector(selector);

    if (isElExistsInDomInitially) {
      resolve(true);
    }

    const observer = new MutationObserver((mutationsList) => {
      let isElExistsInDom = false;

      for (const mutation of mutationsList) {
        if (mutation.type !== 'childList') return

        if (isElExistsInDom) break

        for (const addedNode of mutation.addedNodes) {
          const targetSelector = dataRef ? `[data-ref="${dataRef}"]` : selector;

          if (
            addedNode.parentNode &&
            addedNode.parentNode.querySelector &&
            addedNode.parentNode.querySelector(targetSelector)
          ) {
            isElExistsInDom = true;
            break
          }
        }
      }

      if (isElExistsInDom && !isElementFound) {
        observer.disconnect();
        isElementFound = true;
        resolve(true);
      }
    });
    observer.observe(elementToSearchIn || document.body, {
      childList: true,
      subtree: true
    });
  });

  return Promise.race([timeoutPromise, checkDomUpdatesPromise])
}

function valuesToLowercase(val) {
  if (isString(val)) {
    return val.toLowerCase()
  }

  if (isObject(val)) {
    return Object.entries(val).reduce((acc, [key, objVal]) => {
      return {
        ...acc,
        [key]: valuesToLowercase(objVal)
      }
    }, {})
  }

  if (isArray(val)) {
    return val.map((item) => valuesToLowercase(item))
  }

  return val
}

function getBinaryStringFromBooleanArray(arr) {
  return arr.reduce((acc, value) => acc + +!!value, '')
}

function checkIfFunction(val) {
  return val && (typeof val === 'function' || val instanceof Function)
}

/**
 * This function replace Object key names by another names
 * Implemented only for the 1st level (no deep implementation)
 * @param obj     - Object to remap
 * @param keyMap  - Object map where key - old key name, value - new key name
 * @returns { Object }
 */
function getRemapForNestedField(remap, parentKey) {
  const keyPart = `${parentKey}.`;
  return Object.entries(remap).reduce((acc, [key, value]) => {
    if (key.indexOf(keyPart) === 0) {
      return {
        ...acc,
        [key.split(keyPart)[1]]: value
      }
    }
    return acc
  }, {})
}

function remapObjectKeys(original, remap, modifier = (v) => v) {
  if (isArray(original)) {
    return original.map((v) => remapObjectKeys(v, remap, modifier))
  }

  if (isObject(original)) {
    return Object.entries(original).reduce((acc, [key, value]) => {
      const newValue =
        isObject(value) || isArray(value)
          ? remapObjectKeys(value, getRemapForNestedField(remap, key), modifier)
          : value;
      const newKey = remap[key] || modifier(key);
      return {
        ...acc,
        [newKey]: newValue
      }
    }, {})
  }

  return original
}

function generateHandlersBySettings(handlersSettings, element) {
  return handlersSettings
    .filter(({ conditionFn }) => conditionFn(element))
    .map(({ handlerFn }) => handlerFn)
}

/**
 * This function returns another function which is used to handle parsed HTML
 * elements according to the handlersSettings
 *
 * @param {Object[]}    handlersSettings  - Array of handler settings which should be applied to parsed elements
 * @param {Function}    handlersSettings[].conditionFn  - function which receives parsed element and return TRUE if the element should be processed by the handler
 * @param {Function}    handlersSettings[].handlerFn    - handler which modifies parsed element according to the business logic
 *
 * @returns {Function}  Handler for parsed elements which contains all handlers according to the provided settings
 */
function generateParsedDomElementsHandlerDeep(handlersSettings) {
  return function processElements(elements) {
    if (!elements) return elements

    if (Array.isArray(elements)) {
      /**
       * Notes:
       * - We perform flat() because processElements may return either handled element
       * or array of elements (i.e. dewrap the tag - we return array of it's content)
       * - We perform filter() because processElements may return null after handling the
       * element (i.e. remove some tag)
       *
       * Summary: element handler receives an element as an input and may return:
       * - null
       * - element
       * - array of elements
       * Therefore we can create very flexible handlers.
       */
      return elements
        .map(processElements)
        .flat()
        .filter((el) => !!el)
    }

    const handlers = generateHandlersBySettings(handlersSettings, elements);
    const updatedEl = handlers.length ? pipe(...handlers)(elements) : elements;

    if (!updatedEl) return updatedEl

    if (Array.isArray(updatedEl)) {
      return processElements(updatedEl)
    }

    if (isObject(updatedEl)) {
      return {
        ...updatedEl,
        content: processElements(updatedEl.content)
      }
    }

    return updatedEl
  }
}

/**
 * Used to find if some Object/Array (not primitive) has some parent in the provided
 * data structure (checked by function)
 *
 * @param dataStructure     - any data structure with Objects, Arrays or primitives
 * @param targetElement     - Object or Array (not primitive)
 * @param parentConditionFn - function to check parent item for condition
 * @returns {boolean|*}     - returns true if targetElement has at least one parent item
 *                            which matches the condition
 */
function hasParentInDataStructureDeep({
  dataStructure,
  targetElement,
  parentConditionFn
}) {
  if (!targetElement || !parentConditionFn) return false

  function hasEl(elements, elToFind) {
    if (!elements) {
      return { isTargetElementFound: false, parentCondition: false }
    }

    if (Array.isArray(elements)) {
      return elements.reduce(
        (acc, element) => {
          if (acc.isTargetElementFound && acc.parentCondition) return acc

          const { isTargetElementFound, parentCondition } = hasEl(
            element,
            elToFind
          );
          if (isTargetElementFound && parentCondition) {
            return { isTargetElementFound, parentCondition }
          }
          if (isTargetElementFound) {
            return {
              isTargetElementFound,
              parentCondition: parentConditionFn(element)
            }
          }
          return acc
        },
        { isTargetElementFound: false, parentCondition: false }
      )
    }

    if (isObject(elements)) {
      if (elements === elToFind) {
        return { isTargetElementFound: true, parentCondition: false }
      }

      if (elements.content) {
        return hasEl(elements.content, elToFind)
      }

      return { isTargetElementFound: false, parentCondition: false }
    }

    return { isTargetElementFound: false, parentCondition: false }
  }

  const { isTargetElementFound, parentCondition } = hasEl(
    dataStructure,
    targetElement
  );

  if (!isTargetElementFound) {
    console.log(
      'hasParentInDataStructureDeep():\nElement was not found in the provided data structure'
    );
    return false
  }

  return parentCondition
}

/**
 * Recursively searches for the first focusable element within a given DOM tree.
 * A focusable element is determined based on specific attributes and types, such as inputs,
 * buttons, textareas, links with href attributes, and any element with a positive tabIndex.
 *
 * The function conducts a depth-first search to ensure the first logically focusable element
 * in document order is returned. This is particularly useful for ensuring accessibility standards
 * are met by identifying the initial interaction point for keyboard and screen reader users.
 *
 * @param {Element} element - The root element from which the search for a focusable descendant begins.
 * @returns {Element|null} The first focusable element found within the given element's descendants,
 * or null if no focusable element is found.
 */
function getFirstFocusableDescendant(element) {
  if (!element || !element.childNodes) return null

  for (let i = 0; i < element.childNodes.length; i++) {
    const child = element.childNodes[i];
    const focusable = isFocusable(child)
      ? child
      : getFirstFocusableDescendant(child);

    if (focusable) {
      return focusable
    }
  }
  return null
}

function isFocusable(element) {
  if (element.tabIndex < 0) {
    return false
  }

  if (element.disabled) {
    return false
  }

  const nodeName = element.nodeName && element.nodeName.toLowerCase();

  switch (nodeName) {
    case HTML_TAG.ANCHOR:
      return !!element.href && element.rel !== 'ignore'
    case HTML_TAG.INPUT:
      return element.type !== 'hidden'
    case HTML_TAG.BUTTON:
    case HTML_TAG.SELECT:
    case HTML_TAG.TEXTAREA:
      return true
    default:
      return element.hasAttribute && element.hasAttribute('tabindex')
  }
}

function getWordCount(text) {
  if (!text) return 0

  const delimiters = /[.,:;?!]/gi;
  const oneOrMoreSpaces = / {2,}/gi;

  return text
    .replace(delimiters, ' ')
    .replace(oneOrMoreSpaces, ' ')
    .replace(/[\r\n]+/gi, ' ')
    .split(/\s/)
    .filter(Boolean).length
}

function addStartingSlash(str = '') {
  if (!str) return ''

  return startsWith('/', str) ? str : `/${str}`
}

function addEndingSlash(str = '') {
  if (!str) return ''

  return endsWith('/', str) ? str : `${str}/`
}

const handleMultipleSlashes = url => url.replace(/\/+/g, '/');

function removeEndingSlashes(str = '') {
  if (!str) return ''

  return str.replace(/\/+$/, '')
}

function removeStartingSlash(str = '') {
  return startsWith('/', str) ? str.slice(1) : str
}

function wrapIntoSlashes(str = '') {
  if (!str) return ''

  return addStartingSlash(addEndingSlash(str))
}

function unwrapFromSlashes(str = '') {
  return removeStartingSlash(removeEndingSlashes(str))
}

/**
 * const arr = [ { a: 'a1', b: 'b1' }, { a: 'a2', b: 'b2' } ]
 * arrayOfObjectsToMapByKey({
 *   arrayOfObjects: arr,
 *   keyName: 'a'
 * }
 * Result:
 * { a1: { a: 'a1', b: 'b1' }, a2: { a: 'a2', b: 'b2' } }
 *
 * @param arrayOfObjects
 * @param keyName
 * @param keyHandler
 * @param valueHandler
 * @returns {*}
 */
function arrayOfObjectsToMapByKey({
  arrayOfObjects,
  keyName,
  keyHandler,
  valueHandler
}) {
  return arrayOfObjects.reduce((acc, item) => {
    const key = item[keyName];

    if (!key) return acc

    const handledKey = keyHandler ? keyHandler(key) : key;

    if (!handledKey) return acc

    const value = valueHandler ? valueHandler(item) : item;
    acc[handledKey] = value;

    return acc
  }, {})
}

/**
 * Recursively omits keys from an object or array.
 *
 * This function traverses the input recursively. If the value is an object,
 * it omits any keys that are present in the `keysToOmit` array. If the value
 * is an array, it processes each item in the array recursively.
 *
 * @param {*} value - The input value which can be an object, array, or primitive.
 * @param {string[]} keysToOmit - An array of keys to omit from objects at any depth.
 * @returns {*} - A new value with the specified keys omitted.
 */
function deepOmitKeys(value, keysToOmit) {
  if (Array.isArray(value)) {
    return value.map(item => deepOmitKeys(item, keysToOmit));
  }

  if (value !== null && typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      if (!keysToOmit.includes(key)) {
        acc[key] = deepOmitKeys(value[key], keysToOmit);
      }
      return acc;
    }, {});
  }

  return value;
}


/**
 * Deeply compares two objects while ignoring specified keys.
 *
 * This function removes any keys found in `keysToOmitDeep` from both objects
 * at any level of nesting, and then performs a deep equality check using
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.obj1 - The first object to compare.
 * @param {Object} params.obj2 - The second object to compare.
 * @param {string[]} params.keysToOmitDeep - An array of keys to omit from both objects during comparison.
 * @returns {boolean} - Returns true if the filtered objects are deeply equal, false otherwise.
 */
function deepEqualIgnoringKeysDeep({ obj1, obj2, keysToOmitDeep }) {
  const filteredObj1 = deepOmitKeys(obj1, keysToOmitDeep);
  const filteredObj2 = deepOmitKeys(obj2, keysToOmitDeep);

  return equals(filteredObj1, filteredObj2);
}

function generateCanonicalUrlForStaticPageFactory({
  pathToUrlGenerator,
  defaultEndingSlash
}) {
  return function ({ staticPath, endingSlash = defaultEndingSlash }) {
    const endingSlashString = endingSlash ? '/' : '';
    return pathToUrlGenerator(
      `/${unwrapFromSlashes(staticPath)}${endingSlashString}`
    )
  }
}

const FIRST_PAGINATED_PAGE_NUMBER = 1;
const PAGINATION_REGEX =
  /\/page\/([0-9]+)\/*(?<query>\?[^#\n]+)?(?<hash>#[^\n]+)?$/i;

/**
 * We use path (not fullPath) as we don't need query params or hash for
 * generating a canonical link.
 */
function generatePaginatedCanonicalUrl({
  canonicalUrlWithoutPagination,
  path
}) {
  if (!canonicalUrlWithoutPagination) {
    throw new Error(
      'generatePaginatedCanonicalUrl: canonicalLinkWithoutPagination argument is required'
    )
  }
  if (!path) {
    throw new Error('generatePaginatedCanonicalUrl: path argument is required')
  }

  const paginationMatch = path.match(PAGINATION_REGEX);

  if (!paginationMatch) return canonicalUrlWithoutPagination

  const paginationPageNumber = parseInt(paginationMatch[1]);

  if (paginationPageNumber === FIRST_PAGINATED_PAGE_NUMBER) {
    return canonicalUrlWithoutPagination
  }

  return `${addEndingSlash(
    canonicalUrlWithoutPagination
  )}page/${paginationPageNumber}/`
}

function checkIfNonCanonicalPathFactory({ pathToUrlGenerator }) {
  /**
   * Check if the current path matches the provided canonical URL. If not -
   * redirects to the canonical URL and returns true. Otherwise, returns false.
   *
   * @param {Object} [options]                - Settings for the comparison.
   * @param {boolean} [options.currentPath]   - Current path from the router.
   * @param {boolean} [options.canonicalUrl]  - Expected canonical URL.
   * @param {boolean} [options.decode=false]  - If true, decodes the path using
   *                                            decodeURIComponent function
   * @param {boolean} [options.debug=false]   - If true, enables console logs.
   * @returns {boolean}                       - Returns TRUE if the redirect is
   *                                            called. Otherwise, returns FALSE.
   */
  return function checkIfNonCanonicalPath({
    currentPath,
    canonicalUrl,
    decode = false,
    debug = false
  } = {}) {
    if (!currentPath || !canonicalUrl) {
      throw new Error(
        'redirectIfNonCanonicalUrlFactory: The currentPath and canonicalUrl are required'
      )
    }

    function debugLog(...args) {
      if (!debug) return

      const [first, ...others] = args;
      const firstArg = `checkIfNonCanonicalPath > ${first}`;

      console.log(firstArg, ...others);
    }

    function handlePathDecode(path) {
      debugLog('handlePathDecode (without decode): ', path);
      debugLog('handlePathDecode (with decode): ', decodeURIComponent(path));
      return decode ? decodeURIComponent(path) : path
    }

    const currentUrl = pipe(handlePathDecode, pathToUrlGenerator)(currentPath);
    debugLog('decode: ', decode);
    debugLog('currentPath', currentPath);
    debugLog('currentUrl: ', currentUrl);
    debugLog('canonicalUrl: ', canonicalUrl);
    const handledCanonicalUrl = handlePathDecode(canonicalUrl);
    debugLog('handledCanonicalUrl: ', handledCanonicalUrl);
    debugLog('isEqual: ', currentUrl === handledCanonicalUrl);

    return currentUrl !== handledCanonicalUrl
  }
}

function parseUrl(url) {
  if (!url) return {}

  const regexp = /(?<address>[^#?\n]+)?(?<query>\?[^#\n]+)?(?<hash>#[^\n]+)?/i;
  const result = url.match(regexp);

  if (!result) return {}

  const { address, query, hash } = result.groups;

  return {
    address: address || '',
    hash: hash || '',
    query: query || ''
  }
}

function addAmpSuffix(str = '') {
  if (!str) return ''

  const url = addEndingSlash(str);
  return endsWith('/amp/', url) ? url : `${url}amp/`
}

function removeAmpSuffix(str = '') {
  if (!str) return ''

  const { address: path, hash, query } = parseUrl(str);

  const pathWithoutAmpSuffix = addEndingSlash(path).replace(/\/amp\/$/, '/');

  return `${pathWithoutAmpSuffix}${query || ''}${hash || ''}`
}

function checkIfUrlContainsDomain(url) {
  try {
    const parsedUrl = new URL(url);

    return !!parsedUrl.hostname
  } catch (error) {
    return false
  }
}

function checkIfUrlStartsWithDomain(url, domain) {
  const regexpString = `^${domain.toLowerCase()}`;
  const regexp = new RegExp(regexpString, 'i');

  return !!url.toLowerCase().match(regexp)
}

function removeDomainFromUrl(url, domain) {
  const regexpString = `^${domain.toLowerCase()}`;
  const regexp = new RegExp(regexpString, 'i');

  return url.replace(regexp, '')
}

function checkIfHasProtocol(url) {
  return /https?:\/\//.test(url)
}

function checkIfEmail(url) {
  return /mailto:/.test(url)
}

function checkIfTel(url) {
  return /tel:/.test(url)
}

/**
 * Example of a relative URL: "/all-news/"
 */
function checkIfRelativeUrl(url) {
  return !checkIfHasProtocol(url) &&
    !checkIfEmail(url) &&
    !checkIfTel(url)
}

function getPathNormalizeHandler(addSlash) {
  return pipe(
    addStartingSlash,
    addSlash ? addEndingSlash : removeEndingSlashes,
    handleMultipleSlashes
  )
}

const relativeUrlPattern = /([^?#]*)?(\?[^#]*)?(#.*)?/;

function normalizeUrlSlashForRelativeUrl(url, addSlash) {
  const pathHandler = getPathNormalizeHandler(addSlash);

  const [, basePath = '', query = '', hash = ''] =
  url.match(relativeUrlPattern) || [];

  const normalizedPath = pathHandler(basePath);

  return `${normalizedPath}${query}${hash}`
}

/**
 * Normalizes URL path ending slash while preserving query parameters and hash
 * @param {string} url - The URL to normalize
 * @param {boolean} [addSlash=true] - Whether to add or remove trailing slash
 * @returns {string} Normalized URL
 */
function normalizeUrlSlash(url, addSlash = true) {
  try {
    const pathHandler = getPathNormalizeHandler(addSlash);

    /**
     * We purposefully check for relative urls first, because most
     * of the project's URLs are relative (internal).
     * This is mainly done for optimization purposes since
     * "new URL(url)" is "heavy" operation.
     */
    if (checkIfRelativeUrl(url)) {
      return normalizeUrlSlashForRelativeUrl(url, addSlash)
    }

    const urlObject = new URL(url);

    // Normalize the pathname
    urlObject.pathname = pathHandler(urlObject.pathname);

    return urlObject.toString()
  } catch (error) {
    // Fallback for any URL parsing errors
    return normalizeUrlSlashForRelativeUrl(url, addSlash)
  }
}

/**
 * Normalizes a Vue route path configuration
 * @param {Object} route - Vue route object
 * @param {boolean} [addSlash=true] - Whether to add or remove trailing slash
 * @returns {Object} Normalized route configuration
 */
function normalizeRoutePath(route, addSlash = true) {
  const normalized = { ...route };

  // Normalize the path if it exists
  if (normalized.path) {
    normalized.path = normalizeUrlSlash(normalized.path, addSlash);
  }

  if (normalized.fullPath) {
    normalized.fullPath = normalizeUrlSlash(normalized.fullPath, addSlash);
  }

  // Normalize alias if it exists (can be string or array)
  if (normalized.alias) {
    if (Array.isArray(normalized.alias)) {
      normalized.alias = normalized.alias.map(alias =>
        normalizeUrlSlash(alias, addSlash)
      );
    } else if (typeof normalized.alias === 'string') {
      normalized.alias = normalizeUrlSlash(normalized.alias, addSlash);
    }
  }

  // Recursively normalize child routes
  if (normalized.children) {
    normalized.children = normalized.children.map(child =>
      normalizeRoutePath(child, addSlash)
    );
  }

  return normalized
}

/**
 * Factory function to create a handler for managing redirects.
 *
 * @param {Object} params - The parameters for creating the redirect handler.
 * @param {function} params.clientSideGetRedirectSettings - Function to get redirect settings on the client side.
 * @param {function} params.serverSideGetRedirectSettings - Function to get redirect settings on the server side.
 * @param {boolean} [params.debug=false] - Flag to enable debug logging.
 * @returns {function} An asynchronous function to handle redirects.
 */
function handleRedirectsFactory({
  clientSideGetRedirectSettings,
  serverSideGetRedirectSettings,
  debug = false
}) {
  function debugLog(...args) {
    if (!debug) return

    const [first, ...others] = args;
    const firstArg = `handleRedirects: ${first}`;

    console.log(firstArg, ...others);
  }

  /**
   * Asynchronous function to get redirect settings for a given path.
   *
   * @param {Object} options - The options for retrieving redirect settings.
   * @param {string} options.fullPath - The full path of the current route.
   * @returns {Promise<Object|null>} The final redirect settings if applicable, otherwise `null`.
   */
  return async function handleRedirects(fullPath) {
    debugLog('== handleRedirects() ==');

    let redirectSettings;

    if (process.client) {
      debugLog('Client-side');
      redirectSettings = await clientSideGetRedirectSettings({ fullPath });
    } else {
      debugLog('Server-side');
      redirectSettings = await serverSideGetRedirectSettings({ fullPath });
    }

    return redirectSettings
  }
}

/**
 * Factory function to create a redirect settings handler.
 *
 * @param {Object} [params={}] - Parameters for creating the redirect settings handler.
 * @param {number} [params.maxRedirectCount=5] - Maximum number of redirects to prevent infinite loops.
 * @param {number} [params.fallbackRedirectUrl=5] - A URL to redirect the user to when the redirect count exceeds maxRedirectCount.
 * @param {function[]} [params.redirectHandlers=[]] - Array of handler functions to resolve redirect settings.
 * @param {string} params.domainUrl - The domain URL for identifying external redirects.
 * @param {boolean} [params.debug=false] - Flag to enable debug logging.
 * @returns {function} An asynchronous function to retrieve redirect settings.
 */
function getRedirectSettingsFactory({
  fallbackRedirectUrl,
  redirectHandlers = [],
  domainUrl,
  debug = false
} = {}) {
  function debugLog(...args) {
    if (!debug) return

    const [first, ...others] = args;
    const firstArg = `getRedirectSettings > ${first}`;

    console.log(firstArg, ...others);
  }

  const fallbackRedirectSettings = {
    to: fallbackRedirectUrl,
    type: 302 // temporary redirect
  };

  return async function ({ fullPath }) {
    let finalRedirectSettings = null;
    const fullPathSet = new Set();

    function getFinalRedirectSettings() {
      const copyOfFinalRedirectSettings = finalRedirectSettings;
      finalRedirectSettings = null;
      return copyOfFinalRedirectSettings
    }

    /**
     * Asynchronous function to retrieve redirect settings for a given path.
     *
     * @param {Object} options - Options for retrieving redirect settings.
     * @param {string} options.fullPath - The full path of the current route.
     * @returns {Promise<Object|null>} The final redirect settings if applicable, otherwise `null`.
     */
    async function getRedirectSettings({ fullPath }) {
      debugLog('== getRedirectSettings() ==');
      debugLog('fullPath: ', fullPath);

      if (fullPathSet.has(fullPath)) {
        debugLog('Loop Detected with the following fullPath: ', fullPath);
        debugLog(
          'Return fallback redirect settings: ',
          fallbackRedirectSettings
        );
        return fallbackRedirectSettings
      }

      fullPathSet.add(fullPath);

      const fullPathWithoutAmpSuffix = removeAmpSuffix(fullPath);
      debugLog('fullPathWithoutAmpSuffix: ', fullPathWithoutAmpSuffix);

      let redirectToSettings;

      for (const handler of redirectHandlers) {
        redirectToSettings = await handler({
          fullPath,
          fullPathWithoutAmpSuffix
        });

        if (redirectToSettings) break
      }

      debugLog('redirectToSettings: ', redirectToSettings);

      if (!redirectToSettings) {
        debugLog('finalRedirectSettings: ', finalRedirectSettings);
        return getFinalRedirectSettings()
      }

      const { to } = redirectToSettings;
      debugLog('to: ', to);

      if (to === fullPath) {
        return getFinalRedirectSettings()
      }

      finalRedirectSettings = redirectToSettings;

      const toWithoutDomain = removeDomainFromUrl(to, domainUrl) || '/';
      const isExternal =
        checkIfUrlContainsDomain(to) &&
        !checkIfUrlStartsWithDomain(to, domainUrl);

      debugLog('toWithoutDomain: ', toWithoutDomain);
      debugLog('isExternal: ', isExternal);

      if (isExternal) {
        debugLog('RedirectTo URL is external. Exit recursion.');
        debugLog('finalRedirectSettings: ', finalRedirectSettings);
        return getFinalRedirectSettings()
      }

      debugLog('Run in recursion');

      return getRedirectSettings({ fullPath: toWithoutDomain })
    }

    return getRedirectSettings({ fullPath })
  }
}

/**
 * Compares two route objects and determines changes between them.
 *
 * @param {Object} oldRoute - The previous route object.
 * @param {string} oldRoute.name - The name of the old route.
 * @param {string} oldRoute.path - The path of the old route.
 * @param {string} oldRoute.fullPath - The full path of the old route.
 * @param {Object} newRoute - The current route object.
 * @param {string} newRoute.name - The name of the new route.
 * @param {string} newRoute.path - The path of the new route.
 * @param {string} newRoute.fullPath - The full path of the new route.
 * @param {boolean} [debug=false] - Flag to enable debug logging.
 * @returns {Object} An object with boolean flags indicating changes:
 * - `isRouteNameChanged`: Whether the route name has changed.
 * - `isParentRouteNameChanged`: Whether the parent route name has changed.
 * - `isFromPaginatedToParent`: Whether the route changed from a paginated to a parent route.
 * - `isParentRoutePathChanged`: Whether the parent route path has changed.
 * - `isParentRouteFullPathChanged`: Whether the parent route full path has changed.
 * - `isOnlyHashChanged`: Whether only the hash part of the route has changed.
 */
function compareRoutes(oldRoute, newRoute, debug = false) {
  function debugLog(...args) {
    if (!debug) return

    const [first, ...others] = args;
    const firstArg = `compareRoutes > ${first}`;

    console.log(firstArg, ...others);
  }
  debugLog('== compareRoutes() ==');
  debugLog('oldRoute: ', oldRoute);
  debugLog('newRoute: ', newRoute);
  if (!oldRoute || !newRoute) {
    console.error('oldRoute and newRoute are required!');
    return {}
  }

  try {
    const {
      name: oldRouteName,
      path: oldRoutePath,
      fullPath: oldRouteFullPath
    } = oldRoute;
    const {
      name: newRouteName,
      path: newRoutePath,
      fullPath: newRouteFullPath
    } = newRoute;
    const isRouteNameChanged = oldRouteName !== newRouteName;
    debugLog('oldRouteName: ', oldRouteName);
    debugLog('oldRouteFullPath: ', oldRouteFullPath);
    debugLog('newRouteName: ', newRouteName);
    debugLog('newRouteFullPath: ', newRouteFullPath);

    const pageRouteNamePart = '-page-number';
    const pageRoutePathPart = new RegExp(/page\/\d+\//, 'gmi');
    const pageRouteFullPathPart = new RegExp(/\/?page\/\d+\/?/, 'gmi');
    const isParentRouteNameChanged =
      (newRouteName || '').replace(pageRouteNamePart, '') !==
      (oldRouteName || '').replace(pageRouteNamePart, '');
    const isFromPaginatedToParent =
      newRoutePath ===
      (oldRoutePath.match(pageRoutePathPart) &&
        oldRoutePath.replace(pageRoutePathPart, ''));

    const oldRoutePathWithoutPagination = addEndingSlash(
      addEndingSlash(oldRoutePath).replace(pageRoutePathPart, '')
    );
    const newRoutePathWithoutPagination = addEndingSlash(
      addEndingSlash(newRoutePath).replace(pageRoutePathPart, '')
    );
    const isParentRoutePathChanged =
      oldRoutePathWithoutPagination !== newRoutePathWithoutPagination;

    const oldRouteFullPathWithoutPagination = oldRouteFullPath.replace(
      pageRouteFullPathPart,
      '/'
    );
    const newRouteFullPathWithoutPagination = newRouteFullPath.replace(
      pageRouteFullPathPart,
      '/'
    );

    const isParentRouteFullPathChanged =
      oldRouteFullPathWithoutPagination !== newRouteFullPathWithoutPagination;

    const currentPathWithoutHash = newRoute.fullPath.split('#')[0];
    const previousPathWithoutHash = oldRoute.fullPath.split('#')[0];

    const isOnlyHashChanged = currentPathWithoutHash === previousPathWithoutHash;

    return {
      isRouteNameChanged,
      isParentRouteNameChanged,
      isFromPaginatedToParent,
      isParentRoutePathChanged,
      isParentRouteFullPathChanged,
      isOnlyHashChanged
    }
  } catch (err) {
    console.error(err);
    return {}
  }
}

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function getOffsetString(offsetInHours) {
  if (!offsetInHours) return 'GMT'

  return `GMT${offsetInHours > 0 ? '-' : '+'}${Math.abs(offsetInHours)}`
}

function getParsedDateString(dateStr, forceUtc = false) {
  const parsedDateStr = parseRawDateString(dateStr);

  let OFFSET_IN_HOURS, OFFSET_IN_MILLISECONDS;

  if (process.server || forceUtc) {
    OFFSET_IN_HOURS = 0;
    OFFSET_IN_MILLISECONDS = 0;
  } else {
    /** Show time in user local timezone **/
    const OFFSET_IN_MINUTES = new Date().getTimezoneOffset();
    OFFSET_IN_HOURS = OFFSET_IN_MINUTES / 60;
    OFFSET_IN_MILLISECONDS = OFFSET_IN_MINUTES * 60 * 1000;
  }

  const date = new Date(parsedDateStr);
  date.setTime(date.getTime() - OFFSET_IN_MILLISECONDS);
  const day = date.getDate();
  const month = date.getMonth();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const EEEE = DAYS[date.getDay()];
  const MM = addLeadingZero(month + 1);
  const yyyy = date.getFullYear();
  const dd = addLeadingZero(day);
  const d = day;
  const HH = addLeadingZero(hours);
  const mm = addLeadingZero(minutes);
  const ss = addLeadingZero(seconds);
  const ZZZZ = getOffsetString(OFFSET_IN_HOURS);
  const MMMM = MONTHS[month];

  return {
    EEEE,
    MM,
    MMMM,
    yyyy,
    dd,
    d,
    HH,
    mm,
    ss,
    ZZZZ
  }

  function addLeadingZero(str) {
    return str < 10 ? `0${str}` : str
  }
}

function parseRawDateString(str) {
  return pathOr('0000-00-00T00:00:00', [], str).substring(0, 19)
}

/**
 * example of Page Scheme format: 2021-07-13T14:59:42+00:00
 */
function rawDateUTCStringToPageSchemeFormat(str) {
  return `${parseRawDateString(str)}+00:00`
}

/**
 * Converts an ISO date string to a new date string with a specified timezone offset.
 *
 * @param {string} isoDateStr - The ISO date string to be converted.
 * @param {number} [offsetHours=2] - The timezone offset in hours. Positive values indicate time ahead of UTC, negative values indicate time behind UTC.
 * @returns {string} The formatted date string with the specified timezone offset in the format "YYYY-MM-DDTHH:mm:ss.sss±HH:MM".
 *
 * @example
 * // Returns "2024-05-28T14:05:56.841+02:00"
 * formatDatetimeToOffset("2024-05-28T12:05:56.841Z", 2);
 *
 * @example
 * // Returns "2024-05-28T08:05:56.841-04:00"
 * formatDatetimeToOffset("2024-05-28T12:05:56.841Z", -4);
 */
function formatDateToOffset(isoDateStr, offsetHours = 2) {
  const date = new Date(isoDateStr);

  const offsetMilliseconds = offsetHours * 60 * 60 * 1000;

  // Get the UTC time in milliseconds
  const utcTime = date.getTime();

  // Calculate the new time with the specified offset
  const adjustedTime = new Date(utcTime + offsetMilliseconds);

  // Format the timezone offset
  const offsetSign = offsetHours >= 0 ? '+' : '-';
  const absOffsetHours = Math.abs(offsetHours);
  const formattedOffset = `${offsetSign}${String(absOffsetHours).padStart(
    2,
    '0'
  )}:00`;

  // Format the date to ISO string and adjust the timezone part
  const isoString = adjustedTime.toISOString();
  const formattedDate = isoString.slice(0, -1) + formattedOffset;

  return formattedDate
}

/**
 * Function to check if the date string has a timezone
 * @param {string} dateStr - The date string to check
 * @returns {boolean} - True if the date string has a timezone, otherwise false
 */
function hasTimezone(dateStr) {
  // Check if the date string contains 'Z' or a timezone offset
  return dateStr.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)
}

/**
 * Function to check if the date has a timezone and add UTC if not
 * @param {string} dateStr - The date string to check
 * @returns {string} - The date string with UTC timezone if not present
 */
function ensureUTCForDateWithoutTimezone(dateStr) {
  const date = new Date(dateStr);

  if (isNaN(date.getTime())) {
    return dateStr
  }

  if (hasTimezone(dateStr)) return dateStr

  const { MM, yyyy, dd, HH, mm, ss } = getParsedDateString(dateStr, true);

  return `${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}.000+00:00`
}

export { addAmpSuffix, addEndingSlash, addStartingSlash, arrayOfObjectsToMapByKey, checkIfFunction, checkIfNonCanonicalPathFactory, checkIfRelativeUrl, checkIfUrlContainsDomain, checkIfUrlStartsWithDomain, compareRoutes, deepEqualIgnoringKeysDeep, ensureUTCForDateWithoutTimezone, focusOnElementByDataRef, formatDateToOffset, generateCanonicalUrlForStaticPageFactory, generatePaginatedCanonicalUrl, generateParsedDomElementsHandlerDeep, getBinaryStringFromBooleanArray, getElByDataRef, getFirstFocusableDescendant, getParentWith, getParsedDateString, getRedirectSettingsFactory, getWordCount, handleRedirectsFactory, hasParentInDataStructureDeep, hasParentWith, isArray, isObject, isString, normalizeRoutePath, normalizeUrlSlash, parseUrl, rawDateUTCStringToPageSchemeFormat, remapObjectKeys, removeAmpSuffix, removeDomainFromUrl, removeEndingSlashes, removeStartingSlash, unwrapFromSlashes, valuesToLowercase, waitForElAppearsInDom, wrapIntoSlashes };
